import { createPeriod, listPeriods, type DatoxFile } from "./api"
import { reportRunRequirements, type FactsheetWorkspace, type WorkspaceSave } from "./workspaces"

export function dailyReportPeriod(now = new Date()) {
  const name = `${String(now.getDate()).padStart(2, "0")}-${String(now.getMonth() + 1).padStart(2, "0")}-${now.getFullYear()}`
  return { name, metadata: { period_category: "daily", period: name, year: now.getFullYear() } }
}

export async function ensureDailyReportPeriod(projectID: string) {
  const value = dailyReportPeriod()
  const periods = await listPeriods(projectID)
  return periods.find((period) => period.project_id === projectID && period.status !== "deleted" &&
    period.period_metadata.period_category === "daily" && period.period_metadata.period === value.name)
    ?? await createPeriod(projectID, value.name, value.metadata)
}

export function reportWorkflowFiles(workspace: FactsheetWorkspace, files: DatoxFile[]) {
  if (!workspace.period_id) return []
  return files.filter((file) => file.project_id === workspace.project_id && file.period_id === workspace.period_id && !file.case_id && file.status === "uploaded")
    .map((file) => file.id)
}

export function reportWorkflowInputs(workspace: FactsheetWorkspace, fileIDs: string[]): WorkspaceSave | undefined {
  const ids = [...new Set(fileIDs)].sort()
  if (JSON.stringify([...workspace.config.file_ids].sort()) === JSON.stringify(ids)) return
  return { name: workspace.name, revision: workspace.revision, definition_revision: workspace.definition_revision,
    period_id: workspace.period_id, definition: workspace.definition,
    config: { ...workspace.config, file_ids: ids, bindings: workspace.config.bindings.filter((binding) => ids.includes(binding.file_id)) } }
}

export function reportWorkflowAction(workspace: FactsheetWorkspace) {
  return workspace.config.report_template ? "template" as const : "dashboard" as const
}

// What the report's own run still needs, by the run it starts (the Workflow view's rule): a factsheet's template run
// reads its tables from the template, so it never asks for a category; a blank report's dashboard run does.
// With `fileIDs`: as it would run with those files (Run saves the period's current files first).
export function reportNeeds(workspace: FactsheetWorkspace, fileIDs?: string[]) {
  return reportRunRequirements(fileIDs ? { ...workspace, config: { ...workspace.config, file_ids: fileIDs } } : workspace, reportWorkflowAction(workspace))
}

export function reportWorkflowRequestKey(workspace: FactsheetWorkspace, fileIDs: string[], rerun: boolean) {
  return JSON.stringify([workspace.id, workspace.revision, reportWorkflowAction(workspace), [...new Set(fileIDs)].sort(), rerun])
}
